import { IGroup } from '@entities/groups/contract'
import { combine, createEvent, createStore, sample } from 'effector'
import {
    $allGroups,
    $currentGroupId,
    $goodsGroupMap,
    $groupMap,
    convertGroupToAssociativeArray,
    IGroupMap
} from '@entities/groups/model'
import { useEvent, useStore, useStoreMap } from 'effector-react'
import { cancelWindow } from 'src/features/filter-goods/model/core'
import { $currentPath, $isCatalogPage, $isSearchPage, onRouteChangeComplete } from '@entities/route/model'
import { pushFx } from 'src/infastruct/effector-utils/attach-router'
import { $goods } from '@entities/goods/model'
import { IGroupItem } from '@entities/goods/contract'

export interface ICategoriesItem {
    id: number
    link: string
    title: string
    level: number
}

export interface ITreeCategoriesItem extends ICategoriesItem {
    childs: ITreeCategoriesItem[]
}

export const defaultSearchCategory: ICategoriesItem = {
    id: 0,
    link: '',
    title: 'Все категории',
    level: 0
}

const generateLink = (group: IGroup, groupMap: IGroupMap): string =>
    `${
        group.parentClsGroupId && group.parentClsGroupId > 0
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              generateLink(groupMap[group.parentClsGroupId!], groupMap)
            : ''
    }/${group.id}`

const convertGroupToCategory = (groupMap: IGroupMap, item: IGroup): ITreeCategoriesItem =>
    ({
        id: item.id,
        title: item.title,
        link: generateLink(item, groupMap),
        level: item.level,
        childs: []
    } as ITreeCategoriesItem)

const generateChildCategoriesList = (groupMap: IGroupMap, item: IGroup) => {
    const childs = []
    for (const key in groupMap) {
        const currentNode = groupMap[key]
        if (currentNode.parentClsGroupId === item.id) {
            childs.push(convertGroupToCategory(groupMap, currentNode))
        }
    }
    return childs
}

const wrapParentCategory = (groupMap: IGroupMap, category: ITreeCategoriesItem): ITreeCategoriesItem => {
    let result: ITreeCategoriesItem = category
    let currentNode = groupMap[category.id]
    while (currentNode.parentClsGroupId) {
        const parentNode = groupMap[currentNode.parentClsGroupId]
        const parentCategory = convertGroupToCategory(groupMap, parentNode)
        parentCategory.childs = [result]
        result = parentCategory
        currentNode = parentNode
    }
    return result
}

function generateTreeCategory(groupId: number, allGroups: IGroup[]): ITreeCategoriesItem {
    if (!allGroups || allGroups.length === 0) return {} as ITreeCategoriesItem
    const groupMap = convertGroupToAssociativeArray(allGroups)
    const group = groupMap[groupId]

    let currentCategory = convertGroupToCategory(groupMap, group)
    currentCategory.childs = generateChildCategoriesList(groupMap, group)
    if (!currentCategory.childs || currentCategory.childs.length === 0) {
        const group = groupMap[currentCategory.id]
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        currentCategory = convertGroupToCategory(groupMap, groupMap[group.parentClsGroupId!])
        currentCategory.childs = allGroups
            .filter((x) => x.parentClsGroupId === currentCategory.id)
            .map((gr) => convertGroupToCategory(groupMap, gr))
    }
    // wrap parent categories
    return wrapParentCategory(groupMap, currentCategory)
}

const runGenerateTreeCategories = createEvent()
export const $defaultPath = createStore<string>('')
export const $groupTreeCategories = createStore<ITreeCategoriesItem[]>([])

const toggleFullCategories = createEvent()
const $isOpenedFullCategories = createStore<boolean>(false).on(toggleFullCategories, (state) => !state)

const generateGroupTreeFlat = (groupMap: IGroupMap, groupLocalMap: IGroupMap, group: IGroup): ITreeCategoriesItem[] => {
    const result: ITreeCategoriesItem[] = []
    for (const key in groupLocalMap) {
        const selectedGroup = groupLocalMap[key]
        if (selectedGroup.parentClsGroupId !== group.id) continue
        const category = convertGroupToCategory(groupMap, selectedGroup)
        category.childs = generateGroupTreeFlat(groupMap, groupLocalMap, selectedGroup)
        result.push(category)
    }

    return result
}

/**
 * create categories from goods
 * @param goodsGroupMap
 * @param groupMap groups from api main-groups key/value struct
 */
function generateGoodsGroup(goodsGroupMap: IGroupMap, groupMap: IGroupMap): ITreeCategoriesItem[] {
    const result: ITreeCategoriesItem[] = []

    for (const key in goodsGroupMap) {
        if (goodsGroupMap[key].level === 0) {
            const category = convertGroupToCategory(groupMap, goodsGroupMap[key])
            category.childs = generateGroupTreeFlat(groupMap, goodsGroupMap, goodsGroupMap[key])
            result.push(category)
        }
    }
    return result
}

/**
 * sort categories by select
 * @param categories
 * @param groupMap
 * @param categoryId
 */
function sortCategoriesBySelect(categories: ITreeCategoriesItem[], groupMap: IGroupMap, categoryId: number) {
    let currentLevelGroup = groupMap[categoryId]
    if (categories[0].level > currentLevelGroup.level) return categories
    while (categories[0].level != currentLevelGroup.level) {
        if (!currentLevelGroup.parentClsGroupId) throw new Error('Нет родительской группы')
        currentLevelGroup = groupMap[currentLevelGroup.parentClsGroupId]
    }
    const categoriesWithoutSelect = categories.filter((x) => x.id !== currentLevelGroup.id)
    const result = categories.filter((x) => x.id === currentLevelGroup.id).concat(categoriesWithoutSelect)
    if (result[0].childs && result[0].childs.length > 0) {
        result[0].childs = sortCategoriesBySelect(result[0].childs, groupMap, categoryId)
    }
    return result
}

const selectCategory = createEvent<ICategoriesItem>()
const $actualCategory = createStore<number>(0)

sample({
    clock: selectCategory,
    source: {
        path: $currentPath,
        currentGroupId: $currentGroupId
    },
    filter: ({ path, currentGroupId }, { id }) => {
        if (!path.includes('/catalog/search') && !path.includes('/catalog/suggestions')) return false
        return !(currentGroupId === 0 && id === 0)
    },
    fn: ({ currentGroupId }, category) => {
        if (currentGroupId === category.id) return 0
        return category.id
    },
    target: $currentGroupId
})

sample({
    clock: selectCategory,
    source: {
        path: $currentPath,
        currentGroupId: $currentGroupId
    },
    filter: ({ path, currentGroupId }, { id }) => {
        return (
            path.includes('/catalog/') &&
            !path.includes('/catalog/suggestions') &&
            !path.includes('/search') &&
            !path.includes('/liked') &&
            !path.includes('/promotion') &&
            currentGroupId !== id
        )
    },
    fn: (_, category) => `/catalog${category.link}`,
    target: pushFx
})

const updateCategory = createEvent()

sample({
    clock: runGenerateTreeCategories,
    source: {
        currentGroupId: $currentGroupId,
        isCatalogPage: $isCatalogPage,
        allGroups: $allGroups,
        currentPath: $currentPath
    },
    filter: ({ currentGroupId, isCatalogPage, allGroups }) =>
        currentGroupId > 0 && isCatalogPage && allGroups && allGroups.length > 0,
    fn: ({ currentGroupId, allGroups }) => {
        return [...[generateTreeCategory(currentGroupId as number, allGroups as IGroup[])]] as ITreeCategoriesItem[]
    },
    target: $groupTreeCategories
})

sample({
    clock: $allGroups,
    target: updateCategory
})
const startGenerationCategories = createEvent()
sample({
    clock: startGenerationCategories,
    source: {
        currentPath: $currentPath,
        goodsGroupMap: $goodsGroupMap,
        groupMap: $groupMap
    },
    filter: ({ currentPath }) => {
        return (
            currentPath.includes('/search') ||
            currentPath.includes('/catalog/suggestions') ||
            currentPath.includes('/catalog/liked')
        )
    },
    fn: ({ goodsGroupMap, groupMap }) => generateGoodsGroup(goodsGroupMap, groupMap),
    target: $groupTreeCategories
})
sample({
    clock: $groupMap,
    target: startGenerationCategories
})

sample({
    clock: $currentPath,
    target: startGenerationCategories
})

sample({
    clock: $goods,
    target: startGenerationCategories
})

sample({
    clock: updateCategory,
    source: {
        isSearchPage: $isSearchPage,
        currentPath: $currentPath,
        isCatalogPage: $isCatalogPage,
        currentGroupId: $currentGroupId,
        goods: $goods
    },
    filter: ({ isCatalogPage, isSearchPage, goods }) => isCatalogPage && isSearchPage && goods?.length > 0,
    fn: ({ currentGroupId }) => currentGroupId,
    target: $actualCategory
})

sample({
    clock: $allGroups,
    source: {
        currentPath: $currentPath,
        isCatalogPage: $isCatalogPage,
        goods: $goods
    },
    filter: ({ isCatalogPage, goods }) => isCatalogPage && goods?.length > 0,
    fn: ({ currentPath }) => currentPath,
    target: $defaultPath
})

sample({
    clock: updateCategory,
    source: {
        isCatalogPage: $isCatalogPage,
        goods: $goods
    },
    filter: ({ isCatalogPage, goods }) => isCatalogPage && goods?.length > 0,
    target: runGenerateTreeCategories
})

sample({
    clock: onRouteChangeComplete,
    filter: $isCatalogPage,
    target: runGenerateTreeCategories
})

sample({
    clock: cancelWindow,
    source: {
        currentPath: $currentPath,
        defaultPath: $defaultPath
    },
    filter: ({ currentPath }) =>
        !currentPath.includes('/catalog/search') && !currentPath.includes('/catalog/suggestions'),
    fn: ({ defaultPath }) => defaultPath,
    target: pushFx
})

sample({
    clock: cancelWindow,
    source: {
        currentPath: $currentPath,
        currentGroupId: $currentGroupId,
        actualCategory: $actualCategory
    },
    filter: ({ currentPath, currentGroupId, actualCategory }) =>
        (currentPath.includes('/search') || currentPath.includes('/catalog/')) && actualCategory !== currentGroupId,
    fn: ({ actualCategory }) => actualCategory,
    target: $currentGroupId
})

function sortCategories(items: ITreeCategoriesItem[], priority?: Map<number, boolean>) {
    return items.sort((a: ITreeCategoriesItem, b: ITreeCategoriesItem): number => {
        const isPriorityA = !!priority?.get(a.id)
        const isPriorityB = !!priority?.get(b.id)
        const nameA = a.title.toUpperCase()
        const nameB = b.title.toUpperCase()
        if (isPriorityA) {
            return -1
        }
        if (isPriorityB) {
            return 1
        }
        if (nameA < nameB) {
            return -1
        }
        if (nameA > nameB) {
            return 1
        }
        return 0
    })
}

const treeCategoriesFlatView = (
    categoriesTree: ITreeCategoriesItem[],
    arrayParentMap?: Map<number, boolean>
): ICategoriesItem[] => {
    let result: ICategoriesItem[] = []
    categoriesTree.forEach((x) => {
        result.push(x)
        if (x.childs && x.childs.length > 0) {
            const childs = sortCategories(x.childs, arrayParentMap)
            const childFlat = treeCategoriesFlatView(childs, arrayParentMap)
            result = result.concat(childFlat)
        }
    })
    return result
}

export const $categoriesFlat = combine(
    {
        groupTreeCategories: $groupTreeCategories
    },
    ({ groupTreeCategories }) => {
        return treeCategoriesFlatView(groupTreeCategories)
    }
)

/**
 * цепочка идентификаторов до конечного родителя
 * @param currentGroupId группа от которой собираем цепочку
 * @param groupMap словарь групп
 */
function getParentGroupMap(currentGroupId?: number, groupMap?: IGroupMap): Map<number, boolean> {
    const associative = new Map<number, boolean>()
    if (!currentGroupId) return associative

    associative.set(currentGroupId, true)
    if (!groupMap) return associative

    //return associative

    const currentGroup = groupMap[currentGroupId]
    if (!currentGroup?.parentClsGroupId) return associative
    let parentGroup: IGroup | undefined | null = groupMap[currentGroup.parentClsGroupId]
    if (!parentGroup) return parentGroup
    while (parentGroup) {
        const id: number | null = parentGroup?.parentClsGroupId
        if (!id) {
            parentGroup = null
            break
        }
        associative.set(id, true)
        parentGroup = groupMap[id]
    }
    return associative
}

export const $previewCategories = combine(
    {
        groupTreeCategories: $groupTreeCategories,
        groupMap: $groupMap,
        goodsGroupMap: $goodsGroupMap,
        currentPath: $currentPath,
        currentGroupId: $currentGroupId
    },
    ({ groupTreeCategories, groupMap, currentPath, goodsGroupMap, currentGroupId }) => {
        if (groupTreeCategories.length <= 0) return []
        if (
            (!currentPath.includes('/search') && !currentPath.includes('/catalog/suggestions')) ||
            currentGroupId <= 0
        ) {
            const sortedCategories = currentGroupId
                ? sortCategoriesBySelect(groupTreeCategories, groupMap, currentGroupId)
                : groupTreeCategories

            const categories = treeCategoriesFlatView(sortedCategories)

            return categories.slice(0, 5)
        }

        const parentMap = getParentGroupMap(currentGroupId, groupMap)

        const currentGroup = groupMap[currentGroupId]
        const currentGroupChilds: ITreeCategoriesItem[] = []
        for (const key in goodsGroupMap) {
            if (goodsGroupMap[key].parentClsGroupId === currentGroupId) {
                currentGroupChilds.push(convertGroupToCategory(groupMap, goodsGroupMap[key]))
            }
        }
        if (currentGroupChilds.length > 0 || !currentGroup.parentClsGroupId) {
            const currentCategory = convertGroupToCategory(groupMap, currentGroup)
            currentCategory.childs = sortCategories(currentGroupChilds, parentMap)
            const resultWrapped = wrapParentCategory(goodsGroupMap, currentCategory)
            return [defaultSearchCategory].concat(treeCategoriesFlatView([resultWrapped], parentMap))
        }
        const uppedGroup = groupMap[currentGroup.parentClsGroupId]
        const uppedCategory = convertGroupToCategory(groupMap, uppedGroup)
        const currentUppedGroupChilds = []
        for (const key in goodsGroupMap) {
            if (goodsGroupMap[key].parentClsGroupId === uppedGroup.id) {
                currentUppedGroupChilds.push(convertGroupToCategory(groupMap, goodsGroupMap[key]))
            }
        }
        uppedCategory.childs = sortCategories(currentUppedGroupChilds, parentMap)
        const resultWrapped = wrapParentCategory(goodsGroupMap, uppedCategory)
        return [defaultSearchCategory].concat(treeCategoriesFlatView([resultWrapped], parentMap)).slice(0, 5)
    }
)

const $categoryGroupCombined = combine({
    currentGroupId: $currentGroupId,
    currentPath: $currentPath,
    groupTreeCategories: $categoriesFlat,
    groupMap: $groupMap
})

export const useSelectCategory = () => useEvent(selectCategory)

export const useGroupTreeCategories = () => useStoreMap($categoriesFlat, (state) => state.length)
export const usePreviewCategoriesCount = () => useStoreMap($previewCategories, (state) => state.length)

export const useSelectedGroup = () =>
    useStoreMap($categoryGroupCombined, (state) => {
        return (
            state.groupTreeCategories?.find((x) => x.id === state.currentGroupId) ||
            (defaultSearchCategory as ITreeCategoriesItem)
        )
    })

export const useToggleFullCategories = () => useEvent(toggleFullCategories)
export const useIsOpenedFullCategories = () => useStore($isOpenedFullCategories)
